import React, { useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Dropdown,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ImportOutlined,
  MoreOutlined,
  PlusOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { layerLabel, type Est3WbsLayer } from '@/utils/est3WbsLayers';
import { EpcWbsLibrarySortableTree } from './EpcWbsLibrarySortableTree';
import { EpcEstimateWbsTreeSelect } from '@/components/epc/estimation/EpcEstimateWbsTreeSelect';
import { EpcWbsLibraryPackageGuidedWizard } from './EpcWbsLibraryPackageGuidedWizard';
import { EpcWbsLibraryImportWizard } from './EpcWbsLibraryImportWizard';

export interface EpcWbsLibraryTreeWorkbenchProps {
  packageId: string;
  libraryId: string;
  nodes: any[];
  loading?: boolean;
  readOnly?: boolean;
  selectedNodeId?: string;
  nodeSearch?: string;
  onSelectNode: (id: string | undefined) => void;
  onChanged: () => void;
  onShowShortcuts?: () => void;
}

export const EpcWbsLibraryTreeWorkbench: React.FC<EpcWbsLibraryTreeWorkbenchProps> = ({
  packageId,
  libraryId,
  nodes,
  loading,
  readOnly,
  selectedNodeId,
  nodeSearch,
  onSelectNode,
  onChanged,
  onShowShortcuts,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [editNode, setEditNode] = useState<any | null>(null);
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const layersQ = useQuery({
    queryKey: ['epc-wbs-layers'],
    queryFn: () => epcService.getWbsLayers(),
  });
  const layers = (layersQ.data || []) as Est3WbsLayer[];

  const templatesQ = useQuery({
    queryKey: ['epc-wbs-lib-cost-templates', packageId],
    queryFn: () => epcWbsLibraryService.listCostTemplates(packageId),
    enabled: !!packageId,
  });

  const templateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of templatesQ.data || []) {
      const nid = String(t.library_node_id);
      counts[nid] = (counts[nid] || 0) + 1;
    }
    return counts;
  }, [templatesQ.data]);

  const moveM = useMutation({
    mutationFn: async (
      updates: Array<{ id: string; parentId?: string | null; sortOrder: number }>
    ) => {
      await epcWbsLibraryService.batchReorderPackageNodes(packageId, updates);
    },
    onSuccess: () => {
      message.success('WBS structure updated');
      onChanged();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const openEdit = (row: any) => {
    setEditNode(row);
    editForm.setFieldsValue({
      code: row.code,
      name: row.name,
      parentId: row.parent_id || undefined,
      wbsLayerCode: row.layer_code || undefined,
      description: row.description || undefined,
      shortName: row.short_name || undefined,
    });
  };

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcWbsLibraryService.createPackageNode(packageId, v),
    onSuccess: () => {
      message.success('Node added');
      addForm.resetFields();
      setAddOpen(false);
      onChanged();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchM = useMutation({
    mutationFn: (p: { id: string } & Record<string, unknown>) =>
      epcWbsLibraryService.patchPackageNode(packageId, p.id, p),
    onSuccess: () => {
      message.success('Node updated');
      setEditNode(null);
      onChanged();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteM = useMutation({
    mutationFn: (nodeId: string) => epcWbsLibraryService.deletePackageNode(packageId, nodeId),
    onSuccess: () => {
      message.success('Node removed');
      setEditNode(null);
      if (selectedNodeId) onSelectNode(undefined);
      onChanged();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const layerFields = (
    <>
      <Form.Item name="wbsLayerCode" label="EPC layer">
        <Select
          allowClear
          placeholder="Auto from parent"
          options={layers.map((l) => ({ value: l.code, label: layerLabel(l) }))}
        />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <Input.TextArea rows={2} />
      </Form.Item>
      <Form.Item name="shortName" label="Short name">
        <Input />
      </Form.Item>
    </>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <Space wrap size="small">
          {!readOnly ? (
            <>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                Add node
              </Button>
              {selectedNodeId ? (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    addForm.setFieldsValue({ parentId: selectedNodeId });
                    setAddOpen(true);
                  }}
                >
                  Add child
                </Button>
              ) : null}
              <Dropdown
                menu={{
                  items: [
                    { key: 'guided', icon: <ToolOutlined />, label: 'Guided setup' },
                    { key: 'import', icon: <ImportOutlined />, label: 'Import Excel' },
                  ] as MenuProps['items'],
                  onClick: ({ key }) => {
                    if (key === 'guided') setGuidedOpen(true);
                    if (key === 'import') setImportOpen(true);
                  },
                }}
              >
                <Button size="small" icon={<MoreOutlined />}>
                  Build
                </Button>
              </Dropdown>
              {selectedNodeId ? (
                <>
                  <Button size="small" icon={<EditOutlined />} onClick={() => {
                    const row = nodes.find((n) => String(n.id) === selectedNodeId);
                    if (row) openEdit(row);
                  }}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this node?"
                    onConfirm={() => deleteM.mutate(selectedNodeId)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                </>
              ) : null}
            </>
          ) : null}
        </Space>
      </div>

      <EpcWbsLibrarySortableTree
        nodes={nodes.map((n) => ({
          id: String(n.id),
          code: n.code,
          name: n.name,
          parent_id: n.parent_id,
          layer_code: n.layer_code,
          sort_order: n.sort_order,
          description: n.description,
          short_name: n.short_name,
        }))}
        loading={loading || moveM.isPending}
        selectedId={selectedNodeId}
        searchFilter={nodeSearch}
        readOnly={readOnly}
        templateCounts={templateCounts}
        onShowShortcuts={onShowShortcuts}
        onSelect={(id) => onSelectNode(id)}
        onEdit={readOnly ? undefined : openEdit}
        onDelete={
          readOnly
            ? undefined
            : (nodeId) => {
                Modal.confirm({
                  title: 'Delete this WBS node?',
                  content: 'Only leaf nodes without cost templates can be removed.',
                  okText: 'Delete',
                  okButtonProps: { danger: true },
                  onOk: () => deleteM.mutate(nodeId),
                });
              }
        }
        onMove={(updates) => moveM.mutateAsync(updates)}
      />

      <Drawer
        title="Add WBS node"
        open={addOpen}
        onClose={() => setAddOpen(false)}
        width={440}
        destroyOnClose
        extra={
          <Button type="primary" loading={createM.isPending} onClick={() => addForm.submit()}>
            Add
          </Button>
        }
      >
        <Form form={addForm} layout="vertical" onFinish={(v) => createM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label="Parent">
            <EpcEstimateWbsTreeSelect nodes={nodes} allowClear placeholder="Root if empty" />
          </Form.Item>
          {layerFields}
        </Form>
      </Drawer>

      <Drawer
        title={`Edit ${editNode?.code || 'node'}`}
        open={!!editNode}
        onClose={() => setEditNode(null)}
        width={440}
        destroyOnClose
        extra={
          <Button
            type="primary"
            loading={patchM.isPending}
            onClick={() => editForm.submit()}
          >
            Save
          </Button>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) => editNode && patchM.mutate({ id: String(editNode.id), ...v })}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label="Parent">
            <EpcEstimateWbsTreeSelect nodes={nodes} allowClear />
          </Form.Item>
          {layerFields}
        </Form>
      </Drawer>

      <EpcWbsLibraryPackageGuidedWizard
        open={guidedOpen}
        onClose={() => setGuidedOpen(false)}
        packageId={packageId}
        existingNodes={nodes}
        onCompleted={() => {
          setGuidedOpen(false);
          onChanged();
        }}
      />

      <EpcWbsLibraryImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        libraryId={libraryId}
        packageId={packageId}
        onCompleted={() => {
          setImportOpen(false);
          onChanged();
        }}
      />
    </div>
  );
};
